/**
 * _shared/seoHelpers.ts
 * ────────────────────────────────────────────────────────────────────────
 * Helpers para SEO on-page: meta tags (description, Open Graph, Twitter
 * Card) y Schema.org JSON-LD. Cada template los invoca dentro de un
 * useEffect para mantener el <head> sincronizado con el agente actual.
 *
 * Creado: 17 Abril 2026
 * ────────────────────────────────────────────────────────────────────────
 */

import type { AgentFull } from './types'
import { getAgentUrl } from '@/constants/config'
import { resolveContactChannels, resolveIdentityDisplay } from './agentHelpers'

// ══════════════════════════════════════════════════════════════════════════
// setMetaTag
// ══════════════════════════════════════════════════════════════════════════

/**
 * Inserta o actualiza una <meta> tag en el <head>.
 * Idempotente: si ya existe un meta con el mismo name/property,
 * actualiza su content en lugar de duplicarlo.
 *
 * @param key Valor del atributo name o property.
 * @param content Contenido del meta.
 * @param attrType 'name' para metas estándar, 'property' para Open Graph.
 *
 * @example
 *   setMetaTag('description', 'Agente inmobiliario en CDMX')
 *   setMetaTag('og:image', 'https://...', 'property')
 */
export function setMetaTag(
  key: string,
  content: string,
  attrType: 'name' | 'property' = 'name'
): void {
  if (typeof document === 'undefined') return

  const selector = `meta[${attrType}="${key}"]`
  let meta = document.head.querySelector<HTMLMetaElement>(selector)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attrType, key)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

// ══════════════════════════════════════════════════════════════════════════
// setJsonLd
// ══════════════════════════════════════════════════════════════════════════

/**
 * Inserta o actualiza un <script type="application/ld+json"> en el
 * <head>. Idempotente vía id: llamadas repetidas con el mismo id
 * reescriben el contenido en lugar de duplicar el script.
 *
 * @param data Objeto Schema.org que será serializado a JSON.
 * @param id Id del <script>. Default: 'innovagent-jsonld'.
 *
 * @example
 *   setJsonLd({
 *     '@context': 'https://schema.org',
 *     '@type': 'RealEstateAgent',
 *     name: 'Ana García',
 *   })
 */
export function setJsonLd(
  data: Record<string, unknown>,
  id: string = 'innovagent-jsonld'
): void {
  if (typeof document === 'undefined') return

  let script = document.getElementById(id) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = id
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data)
}

// ══════════════════════════════════════════════════════════════════════════
// setPageSEO
// ══════════════════════════════════════════════════════════════════════════

export interface SetPageSEOOptions {
  /**
   * URL de la imagen para og:image y twitter:image.
   *
   * Cada template decide qué pasar según su filosofía visual:
   *  - Templates person-first: avatar
   *  - Templates brand-first: logo
   *  - Templates property-first: foto de propiedad destacada
   *
   * Si es undefined o null, ambos meta tags se omiten.
   */
  ogImage?: string | null
  /**
   * Override del <title>. Si se omite, se autogenera con el nombre
   * del agente y la primera zona.
   */
  titleOverride?: string
}

/**
 * Establece todos los meta tags y el JSON-LD de la landing pública.
 * Respeta los flags show_* — no expone email si show_email=false,
 * no expone telephone si show_phone=false, etc.
 *
 * Debe llamarse dentro de un useEffect con [agent] como dependencia.
 *
 * @param agent Objeto completo del agente.
 * @param options Overrides opcionales (og:image, título).
 *
 * @example
 *   useEffect(() => {
 *     setPageSEO(agent, { ogImage: img.personal.url ?? undefined })
 *   }, [agent])
 */
export function setPageSEO(
  agent: AgentFull,
  options: SetPageSEOOptions = {}
): void {
  if (typeof document === 'undefined') return

  const contact = resolveContactChannels(agent)
  const identity = resolveIdentityDisplay(agent)

  // ── Nombre "cabeza" para title/descripción/JSON-LD ───────────────────
  // Prioridad para SEO invisible: personal > corporativa > name crudo.
  // Esto NO es una decisión visual — es solo para metadatos.
  const headName = identity.personal.show
    ? identity.personal.displayName
    : identity.corporate.show
      ? identity.corporate.name
      : agent.name

  const firstZone = agent.zones?.[0]
  const autoTitle = firstZone
    ? `${headName} | Asesor Inmobiliario en ${firstZone}`
    : `${headName} | Asesor Inmobiliario`

  document.title = options.titleOverride ?? autoTitle

  // ── Description ──────────────────────────────────────────────────────
  const bioText = (agent.bio ?? '').trim()
  const specialtiesText = (agent.specialties ?? []).slice(0, 3).join(', ')
  const description =
    bioText.length > 0
      ? bioText.slice(0, 155)
      : specialtiesText.length > 0
        ? `Especialista en ${specialtiesText}${firstZone ? ` — ${firstZone}` : ''}`
        : `${headName}, asesor inmobiliario`

  setMetaTag('description', description)

  // ── Open Graph ───────────────────────────────────────────────────────
  const url = getAgentUrl(agent.subdomain)
  setMetaTag('og:title', document.title, 'property')
  setMetaTag('og:description', description, 'property')
  setMetaTag('og:type', 'profile', 'property')
  setMetaTag('og:url', url, 'property')
  setMetaTag('og:locale', 'es_MX', 'property')
  if (options.ogImage) {
    setMetaTag('og:image', options.ogImage, 'property')
  }

  // ── Twitter Card ─────────────────────────────────────────────────────
  setMetaTag('twitter:card', 'summary_large_image')
  setMetaTag('twitter:title', document.title)
  setMetaTag('twitter:description', description)
  if (options.ogImage) {
    setMetaTag('twitter:image', options.ogImage)
  }

  // ── Schema.org JSON-LD (RealEstateAgent) ─────────────────────────────
  const sameAs: string[] = []
  const social = agent.social_media ?? {}
  if (social.facebook) sameAs.push(social.facebook)
  if (social.instagram) sameAs.push(social.instagram)
  if (social.linkedin) sameAs.push(social.linkedin)
  if (social.tiktok) sameAs.push(social.tiktok)
  if (social.youtube) sameAs.push(social.youtube)

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: headName,
    description,
    url,
  }

  if (options.ogImage) jsonLd.image = options.ogImage
  if (contact.showPhone) jsonLd.telephone = contact.phoneNumber
  if (contact.showEmail) jsonLd.email = contact.email
  if (contact.showAddress) {
    jsonLd.address = {
      '@type': 'PostalAddress',
      streetAddress: contact.address,
    }
  }
  if (agent.zones && agent.zones.length > 0) {
    jsonLd.areaServed = agent.zones
  }
  if (agent.specialties && agent.specialties.length > 0) {
    jsonLd.knowsAbout = agent.specialties
  }
  if (sameAs.length > 0) {
    jsonLd.sameAs = sameAs
  }

  setJsonLd(jsonLd)
}
